import { useEffect, useRef, useState } from 'react';

import { toBrandProfileInput } from '../quiz/profile-input';
import type { Answers } from '../quiz/questions';
import { usePostQuiz } from './useOnboardingQueries';
import type { RecoverSession } from './useSessionRecovery';

/** Wait before sending the answers again after the Nth failure in a row; the last delay repeats. */
export const QUIZ_REARM_BACKOFF_MS = [2000, 4000, 8000, 16_000, 30_000] as const;

export const quizRearmDelay = (failures: number): number =>
  QUIZ_REARM_BACKOFF_MS[Math.min(Math.max(failures, 1), QUIZ_REARM_BACKOFF_MS.length) - 1]!;

export interface UseQuizSubmitOptions {
  sessionId: string;
  /** The quiz is done and the BFF session is known to exist (a snapshot is in hand). */
  live: boolean;
  /** `snapshot.quizSubmitted`; undefined until the first snapshot. */
  quizSubmitted: boolean | undefined;
  /** Identifies the BFF session instance (`snapshot.createdAt`): a restart + re-PUT gives a new one. */
  instance: string | undefined;
  answers: Answers;
  recover?: RecoverSession;
}

/**
 * Hands the answers to the BFF once per session instance (POST /quiz is idempotent). A failure is surfaced
 * (`failed`) and the send is re-armed with backoff until it lands, so the chat never silently stops short of
 * a stored baseline. One send per instance and attempt even under StrictMode's doubled effects.
 */
export function useQuizSubmit({ sessionId, live, quizSubmitted, instance, answers, recover }: UseQuizSubmitOptions): { failed: boolean } {
  const { mutate, isError } = usePostQuiz(sessionId, recover);
  const [attempt, setAttempt] = useState(0);
  const sentFor = useRef<string | null>(null);
  const needsSend = live && quizSubmitted === false;

  useEffect(() => {
    if (!needsSend) return;
    const key = `${sessionId}|${instance ?? ''}|${attempt}`;
    if (sentFor.current === key) return;
    sentFor.current = key;
    mutate(toBrandProfileInput(answers));
  }, [needsSend, sessionId, instance, attempt, answers, mutate]);

  useEffect(() => {
    if (!isError || !needsSend) return;
    const timer = window.setTimeout(() => setAttempt((n) => n + 1), quizRearmDelay(attempt + 1));
    return () => window.clearTimeout(timer);
  }, [isError, needsSend, attempt]);

  return { failed: isError && needsSend };
}
